import React, { useState } from 'react';
import { Plus, Send, ChevronDown, Settings, MoreHorizontal, Trash2, Edit3 } from 'lucide-react';

const SessionManagementDemo = () => {
  const [activeOption, setActiveOption] = useState(1);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessions] = useState([
    { id: 1, name: 'Q1 Revenue Analysis', lastMessage: '2 hours ago', messages: 15 },
    { id: 2, name: 'Customer Segmentation', lastMessage: '1 day ago', messages: 8 },
    { id: 3, name: 'Product Performance', lastMessage: '3 days ago', messages: 22 }
  ]);

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-4">Session Management UI Options</h1>
          <div className="flex gap-4 mb-6">
            <button 
              onClick={() => setActiveOption(1)}
              className={`px-4 py-2 rounded-lg transition-all ${activeOption === 1 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-300 hover:bg-slate-700'}`}
            >
              Option 1: Sidebar
            </button>
            <button 
              onClick={() => setActiveOption(2)}
              className={`px-4 py-2 rounded-lg transition-all ${activeOption === 2 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-300 hover:bg-slate-700'}`}
            >
              Option 2: Header Dropdown
            </button>
            <button 
              onClick={() => setActiveOption(3)}
              className={`px-4 py-2 rounded-lg transition-all ${activeOption === 3 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-300 hover:bg-slate-700'}`}
            >
              Option 3: Modal
            </button>
          </div>
        </div>

        {/* Option 1: Collapsible Session Sidebar */}
        {activeOption === 1 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" style={{ height: '600px' }}>
            <div className="flex h-full">
              {/* Session Sidebar */}
              <div className="w-64 bg-slate-800/50 border-r border-slate-700/50 flex flex-col">
                <div className="p-3 border-b border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">Sessions</h3>
                    <button className="w-6 h-6 rounded bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {sessions.map((session, index) => (
                    <div
                      key={session.id}
                      className={`p-2 rounded-md cursor-pointer transition-all duration-200 group ${
                        index === 0 ? 'bg-blue-600/20 border border-blue-500/30' : 'hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{session.name}</p>
                          <p className="text-[10px] text-gray-400">{session.lastMessage}</p>
                        </div>
                        <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-600 rounded">
                          <MoreHorizontal className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b border-slate-700/40">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <span>💡</span>
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Insights Generation Agent</h2>
                      <p className="text-xs text-gray-400">Q1 Revenue Analysis</p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 p-4 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p>Chat messages would appear here</p>
                    <p className="text-sm mt-2">Sessions are easily accessible in sidebar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Option 2: Header-Based Session Management */}
        {activeOption === 2 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" style={{ height: '600px' }}>
            <div className="flex flex-col h-full">
              {/* Header with Session Controls */}
              <div className="p-4 border-b border-slate-700/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <span>💡</span>
                    </div>
                    <h2 className="text-lg font-bold text-white">Insights Generation Agent</h2>
                  </div>
                  
                  {/* Session Controls */}
                  <div className="flex items-center gap-2">
                    {/* New Session Button */}
                    <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center gap-1">
                      <Plus className="w-3 h-3" />
                      New
                    </button>
                    
                    {/* Session Dropdown */}
                    <div className="relative">
                      <button 
                        onClick={() => setShowSessionDropdown(!showSessionDropdown)}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-md flex items-center gap-2 min-w-40"
                      >
                        <span className="truncate">Q1 Revenue Analysis</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      
                      {showSessionDropdown && (
                        <div className="absolute top-full right-0 mt-1 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                          <div className="p-2">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-2 py-1">Recent Sessions</p>
                            {sessions.map((session) => (
                              <div key={session.id} className="p-2 hover:bg-slate-800 rounded-md cursor-pointer group">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate">{session.name}</p>
                                    <p className="text-[10px] text-gray-400">{session.messages} messages • {session.lastMessage}</p>
                                  </div>
                                  <div className="flex opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                                    <button className="p-1 hover:bg-slate-700 rounded">
                                      <Edit3 className="w-3 h-3 text-gray-400" />
                                    </button>
                                    <button className="p-1 hover:bg-slate-700 rounded">
                                      <Trash2 className="w-3 h-3 text-gray-400" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Chat Area */}
              <div className="flex-1 p-4 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p>Chat messages would appear here</p>
                  <p className="text-sm mt-2">Sessions managed via header dropdown</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Option 3: Modal-Based Session Management */}
        {activeOption === 3 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden relative" style={{ height: '600px' }}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="p-4 border-b border-slate-700/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <span>💡</span>
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Insights Generation Agent</h2>
                      <p className="text-xs text-gray-400">Q1 Revenue Analysis</p>
                    </div>
                  </div>
                  
                  {/* Session Button */}
                  <button 
                    onClick={() => setShowSessionModal(!showSessionModal)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-md flex items-center gap-2"
                  >
                    <Settings className="w-3 h-3" />
                    Sessions
                  </button>
                </div>
              </div>
              
              {/* Chat Area */}
              <div className="flex-1 p-4 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p>Chat messages would appear here</p>
                  <p className="text-sm mt-2">Sessions managed via modal popup</p>
                </div>
              </div>
            </div>
            
            {/* Session Modal */}
            {showSessionModal && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md">
                  <div className="p-4 border-b border-slate-700">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">Manage Sessions</h3>
                      <button 
                        onClick={() => setShowSessionModal(false)}
                        className="text-gray-400 hover:text-white"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <button className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center justify-center gap-2 mb-4">
                      <Plus className="w-4 h-4" />
                      New Session
                    </button>
                    
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {sessions.map((session, index) => (
                        <div key={session.id} className={`p-3 rounded-lg cursor-pointer group ${
                          index === 0 ? 'bg-blue-600/20 border border-blue-500/30' : 'bg-slate-800 hover:bg-slate-700'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">{session.name}</p>
                              <p className="text-xs text-gray-400">{session.messages} messages • {session.lastMessage}</p>
                            </div>
                            <div className="flex opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                              <button className="p-1 hover:bg-slate-600 rounded">
                                <Edit3 className="w-3 h-3 text-gray-400" />
                              </button>
                              <button className="p-1 hover:bg-slate-600 rounded">
                                <Trash2 className="w-3 h-3 text-gray-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comparison Summary */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <h3 className="font-semibold text-white mb-2">Option 1: Sidebar</h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p>✅ Most like ChatGPT</p>
              <p>✅ Always visible sessions</p>
              <p>✅ Easy switching</p>
              <p>⚠️ Takes screen space</p>
            </div>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <h3 className="font-semibold text-white mb-2">Option 2: Header</h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p>✅ Compact design</p>
              <p>✅ Quick access</p>
              <p>✅ Shows current session</p>
              <p>⚠️ Dropdown can be long</p>
            </div>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <h3 className="font-semibold text-white mb-2">Option 3: Modal</h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p>✅ Clean interface</p>
              <p>✅ Full session management</p>
              <p>✅ No space taken</p>
              <p>⚠️ Extra click needed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagementDemo;